import { StrictMode, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';

import { installHardLogout } from '@/app/installHardLogout';
import { RouterRootFallback } from '@/app/RouterRootFallback';
import { router } from '@/app/router';
import { I18nProvider } from '@/app/providers/I18nProvider';
import { QueryProvider } from '@/app/providers/QueryProvider';
import { UiProvider } from '@/app/providers/UiProvider';
import { hasTokens } from '@/shared/lib/auth';
import { getPersistedUserSnapshot } from '@/shared/lib/auth/userSnapshot';
import { useSessionStore } from '@/shared/lib/auth/session';
import { Toaster } from '@/shared/ui';
import '@/styles/globals.css';

installHardLogout();

/** Restore session user from localStorage before React mounts (pairs with access/refresh tokens). */
function hydrateUserSnapshotFromStorage(): void {
  if (typeof window === 'undefined') return;
  if (!hasTokens()) return;
  const snap = getPersistedUserSnapshot();
  if (snap) {
    useSessionStore.getState().setUser(snap);
  }
}

hydrateUserSnapshotFromStorage();

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('#root element not found in index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <I18nProvider>
      <QueryProvider>
        <UiProvider>
          <Toaster />
          <Suspense fallback={<RouterRootFallback />}>
            <RouterProvider router={router} />
          </Suspense>
        </UiProvider>
      </QueryProvider>
    </I18nProvider>
  </StrictMode>,
);
